import "./App.css";
import React, { Suspense, useEffect, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { useColorTheme } from "./Context/ThemeContext/ThemeContext";
import CssBaseline from "@mui/material/CssBaseline";
import { Box } from "@mui/material";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { AdminRoutes, SuperAdminRoutes } from "./AllRoutes/AllRoutes";
import SnackBarContextProvider from "./Context/SnackBarContext/SnackBarContext";
import SnackBar from "./Components/SnackBar/SnackBar";
import { useDispatch } from "react-redux";
import { fetchUserRoles } from "./Redux/Slice/Settings/UserSlice/UserSlice";
import LoginContent from "./Pages/Login/LoginContent";
import AppContextProvider from "./Context/TourContext/TourContext";
import FacebookPageDetailContextProvider from "./Context/FacebookPageDetailContext/FacebookPageDetailContext";
import { SignalRProvider } from "./Context/SignalRContext/SignalRContext";
import NotificationContextProvider from "./Context/NotificationContext/NotificationContext";
import PageNumberContextProvider from "./Context/PageNumberContext/PageNumberContext";
import SideBarContextProvider from "./Context/SideBarContext/SideBarContext";
import { useSession } from "./Context/SessionContext";
import SkeletonLayout from "./Pages/UserContainer/SkeletonLayout";
import { useSubscription } from "./Context/SubscriptionContext";
import NoAccess from "./Utils/NoAccess";
import { EvolutionSocketProvider } from "./Context/EvolutionSocketContext/EvolutionSocketContext ";
import PaymentReminderDemo from "./Components/PaymentReminderModel/PaymentReminderModel";
import AppLoader from "./Components/AppLoader/AppLoader";
// import { WhatsappProvider } from "./Context/WhatsappContext";

function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { setSubscriptionError } = useSubscription();
  const { session, loading, setLoading, reponseErorr } = useSession();

  const IsSuperAdmin = session?.IsSuperAdmin;
  const isAdmin = session?.isAdmin;
  const loginId = session?.LoginId;
  const userRoles = session?.user; // assuming this is your roles array
  const token = sessionStorage.getItem("access_token");
  const [allRoutes, setAllRoutes] = useState([]);

  // Initialize FCM for push notifications

  useEffect(() => {
    if (loginId) {
      dispatch(fetchUserRoles(loginId))
        .unwrap()
        .catch((error) => {
          setSubscriptionError(error);
        });
    }
  }, [loginId, dispatch, setSubscriptionError]);

  // Set routes based on role (SuperAdmin/Admin)
  useEffect(() => {
    const hasRole = (route) => {
      if (!route?.roleName) return true;
      if (Array.isArray(route.roleName)) {
        return route.roleName.some((r) => userRoles?.includes(r));
      }
      return userRoles?.includes(route.roleName);
    };

    if (IsSuperAdmin === true) {
      const superAdminRoutes = SuperAdminRoutes.filter(hasRole);
      setAllRoutes(superAdminRoutes);

      setLoading(false);
    } else {
      const adminRoutes = AdminRoutes.filter(hasRole);
      setAllRoutes(adminRoutes);

      setLoading(false);
    }
  }, [IsSuperAdmin, isAdmin, userRoles]);

  const isValidRoute = allRoutes?.some((route) => route.path === location.pathname);
  const { theme, themeMode } = useColorTheme();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          backgroundColor: (theme) => theme.palette.background.main,
          minHeight: "100vh",
          transition: "background-color 0.25s ease",
        }}
        className={`App theme-${themeMode}`}
        data-theme={themeMode}
      >
        <SnackBarContextProvider>
          <FacebookPageDetailContextProvider>
            <AppContextProvider>
              <EvolutionSocketProvider>
                <SignalRProvider>
                  <NotificationContextProvider>
                    <PageNumberContextProvider>
                      <SideBarContextProvider>
                        {loading ? (
                          <SkeletonLayout />
                        ) : (
                          <Suspense fallback={<AppLoader />}>
                            <Routes>
                              {allRoutes
                                ?.filter((route) => route.public)
                                .map((route, idx) => (
                                  <Route key={idx} path={route.path} element={route.component} />
                                ))}

                              {allRoutes?.map((route, idx) => (
                                <Route key={idx} path={route.path} element={route.component} />
                              ))}

                              {/* if current user api give error */}
                              {allRoutes?.length === 0 && reponseErorr && (
                                <Route
                                  path="*"
                                  element={
                                    <Navigate to="/" replace state={{ from: location.pathname }} />
                                  }
                                />
                              )}

                              {/* if user is not valid */}
                              {allRoutes?.length === 0 && (
                                <Route path="/" element={<LoginContent />} />
                              )}

                              {/* if has invalid route and not in refresh phase(wait for context values to decide that it is valid or invalid route) */}
                              {!isValidRoute && token && userRoles && allRoutes?.length > 0 && (
                                <Route path="*" element={<NoAccess />} />
                              )}

                              {/* if user is not logged in and tries to access an invalid/protected route */}
                              {!isValidRoute && !token && allRoutes?.length > 0 && (
                                <Route
                                  path="*"
                                  element={
                                    <Navigate to="/" replace state={{ from: location.pathname }} />
                                  }
                                />
                              )}
                            </Routes>
                          </Suspense>
                        )}
                        <SnackBar />
                      </SideBarContextProvider>
                    </PageNumberContextProvider>
                  </NotificationContextProvider>
                </SignalRProvider>
              </EvolutionSocketProvider>
            </AppContextProvider>
          </FacebookPageDetailContextProvider>
          <PaymentReminderDemo />
        </SnackBarContextProvider>
      </Box>
    </ThemeProvider>
  );
}

export default App;
